import { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Pencil, Eye, EyeOff, Info, FileText, List, Hash, Calendar, CheckSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CustomFieldType, CustomFieldDefinition } from "@/types/custom-fields";
import { useCustomFields } from "@/hooks/use-custom-fields";
import { useScreenMode } from "@/providers/ModeProvider";

// Fixed field definition (extended to support backend ID for live mode)
interface FixedFieldDefinition {
  id: string;
  fieldName: string;
  fieldLabel: string;
  fieldType: CustomFieldType;
  isRequired: boolean;
  isActive: boolean;
  showInTable: boolean;
  dropdownOptions: string[];
  displayOrder: number;
  customFieldDefinitionID?: number; // Backend ID for live mode
}

// Generate fixed fields pool
const generateFixedFields = (): FixedFieldDefinition[] => {
  const fields: FixedFieldDefinition[] = [];

  // Text Fields (15)
  for (let i = 1; i <= 15; i++) {
    fields.push({
      id: `text-${i}`,
      fieldName: `textField${i}`,
      fieldLabel: `Text Field ${i}`,
      fieldType: "Text",
      isRequired: false,
      isActive: false,
      showInTable: false,
      dropdownOptions: [],
      displayOrder: i,
    });
  }

  // Dropdown Fields (8)
  for (let i = 1; i <= 8; i++) {
    fields.push({
      id: `dropdown-${i}`,
      fieldName: `dropdownField${i}`,
      fieldLabel: `Dropdown Field ${i}`,
      fieldType: "Dropdown",
      isRequired: false,
      isActive: false,
      showInTable: false,
      dropdownOptions: [],
      displayOrder: 15 + i,
    });
  }

  // Number Fields (5)
  for (let i = 1; i <= 5; i++) {
    fields.push({
      id: `number-${i}`,
      fieldName: `numberField${i}`,
      fieldLabel: `Number Field ${i}`,
      fieldType: "Number",
      isRequired: false,
      isActive: false,
      showInTable: false,
      dropdownOptions: [],
      displayOrder: 23 + i,
    });
  }

  // Date Fields (5)
  for (let i = 1; i <= 5; i++) {
    fields.push({
      id: `date-${i}`,
      fieldName: `dateField${i}`,
      fieldLabel: `Date Field ${i}`,
      fieldType: "Date",
      isRequired: false,
      isActive: false,
      showInTable: false,
      dropdownOptions: [],
      displayOrder: 28 + i,
    });
  }

  // Boolean Fields (5)
  for (let i = 1; i <= 5; i++) {
    fields.push({
      id: `boolean-${i}`,
      fieldName: `booleanField${i}`,
      fieldLabel: `Yes/No Field ${i}`,
      fieldType: "Boolean",
      isRequired: false,
      isActive: false,
      showInTable: false,
      dropdownOptions: [],
      displayOrder: 33 + i,
    });
  }

  return fields;
};

const FIELD_TYPE_INFO = {
  Text: { label: "Text", description: "Single line text input", icon: <FileText className="h-4 w-4" /> },
  Number: { label: "Number", description: "Numeric input", icon: <Hash className="h-4 w-4" /> },
  Date: { label: "Date", description: "Date picker", icon: <Calendar className="h-4 w-4" /> },
  Dropdown: { label: "Dropdown", description: "Select from options", icon: <List className="h-4 w-4" /> },
  Boolean: { label: "Yes/No", description: "True/false toggle", icon: <CheckSquare className="h-4 w-4" /> },
};

export function CustomFieldsPage() {
  // Backend hook for live mode
  const {
    definitions: backendDefinitions,
    isLoading: isBackendLoading,
    fetchDefinitions,
    createDefinition,
    updateDefinition,
    deleteDefinition,
  } = useCustomFields();

  // Mode context for demo/live toggle (screen-specific)
  const { setMode, isDemo, isLive } = useScreenMode("custom-fields");
  const isLiveMode = isLive; // Alias for compatibility with existing code

  // Load demo fields from localStorage (simulating backend)
  const [demoFields, setDemoFields] = useState<FixedFieldDefinition[]>(() => {
    const saved = localStorage.getItem("customFields");
    return saved ? JSON.parse(saved) : generateFixedFields();
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<FixedFieldDefinition | null>(null);
  const [dropdownInput, setDropdownInput] = useState("");

  // Save demo fields to localStorage whenever they change
  useEffect(() => {
    if (!isLiveMode) {
      localStorage.setItem("customFields", JSON.stringify(demoFields));
    }
  }, [demoFields, isLiveMode]);

  // Handle mode toggle - uses shared ModeProvider
  const handleModeToggle = useCallback(
    async (newIsLive: boolean) => {
      if (newIsLive) {
        // Switching to Live mode - fetch data from backend
        await fetchDefinitions();
      }
      setMode(newIsLive ? "live" : "demo");
    },
    [fetchDefinitions, setMode],
  );

  // Map backend definitions to FixedFieldDefinition format for display
  // In live mode, we show all fixed fields and merge with backend data
  const backendFieldsAsDemoFormat = useMemo((): FixedFieldDefinition[] => {
    // Start with the fixed fields pool
    const fixedFields = generateFixedFields();

    // Create a map of backend definitions by fieldName for quick lookup
    const backendByFieldName = new Map<string, CustomFieldDefinition>();
    backendDefinitions.forEach((def) => {
      backendByFieldName.set(def.fieldName, def);
    });

    // Merge fixed fields with backend data
    return fixedFields.map((fixedField) => {
      const backendDef = backendByFieldName.get(fixedField.fieldName);
      if (backendDef) {
        // Field exists in backend - use backend data
        return {
          ...fixedField,
          id: `backend-${backendDef.customFieldDefinitionID}`,
          fieldLabel: backendDef.fieldLabel,
          isRequired: backendDef.isRequired,
          isActive: backendDef.isActive,
          dropdownOptions: backendDef.dropdownOptions ? JSON.parse(backendDef.dropdownOptions) : [],
          displayOrder: backendDef.displayOrder,
          customFieldDefinitionID: backendDef.customFieldDefinitionID,
        };
      }
      // Field doesn't exist in backend - show as disabled
      return {
        ...fixedField,
        isActive: false,
        customFieldDefinitionID: undefined,
      };
    });
  }, [backendDefinitions]);

  // Get the current data source based on mode
  const fields = useMemo(() => {
    return isLiveMode ? backendFieldsAsDemoFormat : demoFields;
  }, [isLiveMode, backendFieldsAsDemoFormat, demoFields]);

  // Wrapper to set fields based on mode
  const setFields = useCallback(
    (updater: FixedFieldDefinition[] | ((prev: FixedFieldDefinition[]) => FixedFieldDefinition[])) => {
      if (isLiveMode) {
        // In live mode, we don't update local state directly
        // Updates go through the backend mutations
        return;
      }
      if (typeof updater === "function") {
        setDemoFields(updater);
      } else {
        setDemoFields(updater);
      }
    },
    [isLiveMode],
  );

  // Group fields by type
  const fieldsByType = {
    Text: fields.filter((f) => f.fieldType === "Text"),
    Dropdown: fields.filter((f) => f.fieldType === "Dropdown"),
    Number: fields.filter((f) => f.fieldType === "Number"),
    Date: fields.filter((f) => f.fieldType === "Date"),
    Boolean: fields.filter((f) => f.fieldType === "Boolean"),
  };

  // Count active fields
  const activeFieldsCount = fields.filter((f) => f.isActive).length;
  const totalFieldsCount = fields.length;

  const handleOpenDialog = (field: FixedFieldDefinition) => {
    setEditingField(field);
    setDropdownInput(field.dropdownOptions.join(", "));
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingField(null);
    setDropdownInput("");
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!editingField) return;

    // Validation
    if (!editingField.fieldLabel.trim()) {
      toast.error("Field label is required");
      return;
    }

    if (editingField.fieldType === "Dropdown" && editingField.dropdownOptions.length === 0) {
      toast.error("Dropdown options are required");
      return;
    }

    if (isLiveMode && editingField.customFieldDefinitionID) {
      // Live mode - use backend mutation
      setIsSaving(true);
      try {
        const success = await updateDefinition(editingField.customFieldDefinitionID, {
          fieldName: editingField.fieldName,
          fieldLabel: editingField.fieldLabel,
          fieldType: editingField.fieldType,
          isRequired: editingField.isRequired,
          dropdownOptions: editingField.dropdownOptions,
        });
        if (success) {
          await fetchDefinitions(); // Refresh data from backend
          handleCloseDialog();
        }
      } finally {
        setIsSaving(false);
      }
    } else {
      // Demo mode - update local state
      setDemoFields(demoFields.map((f) => (f.id === editingField.id ? editingField : f)));
      toast.success(`Field "${editingField.fieldLabel}" updated successfully`);
      handleCloseDialog();
    }
  };

  const [isTogglingField, setIsTogglingField] = useState<string | null>(null);

  const handleToggleActive = async (fieldId: string) => {
    const field = fields.find((f) => f.id === fieldId);
    if (!field) return;

    const newActive = !field.isActive;

    if (isLiveMode) {
      setIsTogglingField(fieldId);
      try {
        if (field.customFieldDefinitionID) {
          // Field exists in backend - update isActive status
          if (newActive) {
            // Re-enable the field
            const success = await updateDefinition(field.customFieldDefinitionID, {
              fieldName: field.fieldName,
              fieldLabel: field.fieldLabel,
              fieldType: field.fieldType,
              isRequired: field.isRequired,
              dropdownOptions: field.dropdownOptions,
            });
            if (success) {
              await fetchDefinitions();
              toast.success("Field enabled");
            }
          } else {
            // Disable the field - delete from backend
            const success = await deleteDefinition(field.customFieldDefinitionID);
            if (success) {
              await fetchDefinitions();
              toast.success("Field disabled");
            }
          }
        } else {
          // Field doesn't exist in backend - create new definition
          if (newActive) {
            const result = await createDefinition({
              fieldName: field.fieldName,
              fieldLabel: field.fieldLabel,
              fieldType: field.fieldType,
              isRequired: field.isRequired,
              dropdownOptions: field.dropdownOptions.length > 0 ? field.dropdownOptions : undefined,
            });
            if (result) {
              await fetchDefinitions();
              toast.success("Field enabled");
            }
          }
        }
      } catch (err) {
        console.error("Failed to toggle field active state:", err);
        toast.error("Failed to toggle field");
      } finally {
        setIsTogglingField(null);
      }
    } else {
      // Demo mode - update local state
      setDemoFields(
        demoFields.map((f) => {
          if (f.id === fieldId) {
            toast.success(newActive ? `Field enabled` : `Field disabled`);
            return { ...f, isActive: newActive };
          }
          return f;
        }),
      );
    }
  };

  const handleToggleShowInTable = (fieldId: string) => {
    // showInTable is a frontend-only feature for now, so we only update local state
    if (isLiveMode) {
      // In live mode, we can still track showInTable locally
      // This could be extended to save to backend if needed
      toast.info("Show in table preference saved locally");
    }

    if (!isLiveMode) {
      setDemoFields(
        demoFields.map((f) => {
          if (f.id === fieldId) {
            const newShow = !f.showInTable;
            toast.success(newShow ? `Field will appear in table` : `Field hidden from table`);
            return { ...f, showInTable: newShow };
          }
          return f;
        }),
      );
    }
  };

  const handleFieldLabelChange = async (fieldId: string, newLabel: string) => {
    const field = fields.find((f) => f.id === fieldId);
    if (!field) return;

    if (isLiveMode && field.customFieldDefinitionID) {
      // In live mode, we'll update on blur or save, not on every keystroke
      // For now, just update the editing field if dialog is open
      return;
    }

    // Demo mode - update local state
    setDemoFields(demoFields.map((f) => (f.id === fieldId ? { ...f, fieldLabel: newLabel } : f)));
  };

  const handleDropdownInputChange = (value: string) => {
    setDropdownInput(value);
    const options = value
      .split(",")
      .map((opt) => opt.trim())
      .filter((opt) => opt.length > 0);
    if (editingField) {
      setEditingField({ ...editingField, dropdownOptions: options });
    }
  };

  const renderFieldRow = (field: FixedFieldDefinition) => (
    <TableRow key={field.id} className={field.isActive ? "" : "opacity-50"}>
      <TableCell className="w-12">
        {isTogglingField === field.id ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Switch
            checked={field.isActive}
            onCheckedChange={() => handleToggleActive(field.id)}
            disabled={isTogglingField !== null}
          />
        )}
      </TableCell>
      <TableCell className="font-mono text-sm">{field.fieldName}</TableCell>
      <TableCell>
        <Input
          value={field.fieldLabel}
          onChange={(e) => {
            if (!isLiveMode) {
              // Demo mode - update local state immediately
              setDemoFields(demoFields.map((f) => (f.id === field.id ? { ...f, fieldLabel: e.target.value } : f)));
            }
            // In live mode, inline editing is disabled - use the configure dialog instead
          }}
          className="h-8"
          disabled={!field.isActive || isLiveMode}
          placeholder={isLiveMode ? "Use Configure button to edit" : "Enter label"}
        />
      </TableCell>
      <TableCell>
        <Badge variant={field.isRequired ? "destructive" : "outline"}>
          {field.isRequired ? "Required" : "Optional"}
        </Badge>
      </TableCell>
      <TableCell>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleToggleShowInTable(field.id)}
          disabled={!field.isActive || isLiveMode}
        >
          {field.showInTable ? (
            <Eye className="h-4 w-4 text-green-600" />
          ) : (
            <EyeOff className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {field.fieldType === "Dropdown" && field.dropdownOptions.length > 0
          ? field.dropdownOptions.slice(0, 2).join(", ") + (field.dropdownOptions.length > 2 ? "..." : "")
          : "—"}
      </TableCell>
      <TableCell className="text-right">
        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(field)} disabled={!field.isActive}>
          <Pencil className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Custom Fields</h1>
            {isBackendLoading ? (
              <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Loading...
              </Badge>
            ) : isDemo ? (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                Demo Mode
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                Live Mode
              </Badge>
            )}
            <div className="flex items-center gap-2 ml-2">
              <span className={`text-sm ${isDemo ? "text-muted-foreground" : "font-medium"}`}>Demo</span>
              <Switch
                checked={isLive}
                onCheckedChange={(checked) => handleModeToggle(checked)}
                disabled={isBackendLoading}
              />
              <span className={`text-sm ${isLive ? "font-medium" : "text-muted-foreground"}`}>Live</span>
            </div>
          </div>
          <p className="text-muted-foreground mt-2">
            Configure custom attributes for your stock items. Enable and customise the fields you need.
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold">
            {activeFieldsCount} / {totalFieldsCount}
          </div>
          <div className="text-sm text-muted-foreground">Fields Active</div>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <h4 className="font-semibold text-sm">Fixed Fields Pool</h4>
              <p className="text-sm text-muted-foreground">
                This system provides <strong>38 pre-configured custom fields</strong> (15 Text, 8 Dropdown, 5 Number, 5
                Date, 5 Yes/No). Simply <strong>enable</strong> the fields you need, <strong>rename</strong> them to
                match your business, and <strong>configure</strong> their options. Toggle the eye icon to show fields in
                the stock items table.
              </p>
              <div className="flex gap-4 text-xs text-muted-foreground pt-2">
                <div className="flex items-center gap-1">
                  <Switch className="scale-75" /> Enable/Disable
                </div>
                <div className="flex items-center gap-1">
                  <Eye className="h-3 w-3" /> Show in Table
                </div>
                <div className="flex items-center gap-1">
                  <Pencil className="h-3 w-3" /> Configure
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fields by Type */}
      <Tabs defaultValue="Text" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          {Object.entries(FIELD_TYPE_INFO).map(([type, info]) => {
            const count = fieldsByType[type as CustomFieldType].filter((f) => f.isActive).length;
            const total = fieldsByType[type as CustomFieldType].length;
            return (
              <TabsTrigger key={type} value={type} className="relative">
                <span className="mr-2">{info.icon}</span>
                {info.label}
                <Badge variant="secondary" className="ml-2 text-xs">
                  {count}/{total}
                </Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.entries(fieldsByType).map(([type, typeFields]) => (
          <TabsContent key={type} value={type}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span>{FIELD_TYPE_INFO[type as CustomFieldType].icon}</span>
                  {FIELD_TYPE_INFO[type as CustomFieldType].label} Fields
                  <Badge variant="outline">
                    {typeFields.filter((f) => f.isActive).length} of {typeFields.length} active
                  </Badge>
                </CardTitle>
                <CardDescription>
                  {FIELD_TYPE_INFO[type as CustomFieldType].description}. Enable the fields you need and customize their
                  labels.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Active</TableHead>
                      <TableHead>Field Name</TableHead>
                      <TableHead>Custom Label</TableHead>
                      <TableHead>Required</TableHead>
                      <TableHead className="w-20">In Table</TableHead>
                      <TableHead>Options</TableHead>
                      <TableHead className="text-right w-20">Configure</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>{typeFields.map(renderFieldRow)}</TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Configure Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Configure Field</DialogTitle>
            <DialogDescription>Customize this field to match your business needs.</DialogDescription>
          </DialogHeader>

          {editingField && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Field Name (Internal)</Label>
                  <Input value={editingField.fieldName} disabled className="font-mono" />
                  <p className="text-xs text-muted-foreground">Cannot be changed</p>
                </div>

                <div className="space-y-2">
                  <Label>Field Type</Label>
                  <Input value={FIELD_TYPE_INFO[editingField.fieldType].label} disabled />
                  <p className="text-xs text-muted-foreground">Cannot be changed</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fieldLabel">
                  Display Label <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="fieldLabel"
                  placeholder="e.g., Color, Batch Number, Expiry Date"
                  value={editingField.fieldLabel}
                  onChange={(e) => setEditingField({ ...editingField, fieldLabel: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">This is what users will see</p>
              </div>

              {editingField.fieldType === "Dropdown" && (
                <div className="space-y-2">
                  <Label htmlFor="dropdownOptions">
                    Dropdown Options <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="dropdownOptions"
                    placeholder="e.g., Red, Blue, Green, Yellow"
                    value={dropdownInput}
                    onChange={(e) => handleDropdownInputChange(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Comma-separated list of options</p>
                  {editingField.dropdownOptions.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {editingField.dropdownOptions.map((opt, idx) => (
                        <Badge key={idx} variant="secondary">
                          {opt}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="isRequired">Required Field</Label>
                    <p className="text-xs text-muted-foreground">Users must provide a value</p>
                  </div>
                  <Switch
                    id="isRequired"
                    checked={editingField.isRequired}
                    onCheckedChange={(checked) => setEditingField({ ...editingField, isRequired: checked })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="showInTable">Show in Stock Items Table</Label>
                    <p className="text-xs text-muted-foreground">Display as a column in the table</p>
                  </div>
                  <Switch
                    id="showInTable"
                    checked={editingField.showInTable}
                    onCheckedChange={(checked) => setEditingField({ ...editingField, showInTable: checked })}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
